import React from 'react';

export interface FAQItem {
  question: string;
  answer?: string;
  answerRichText?: any; // Contentful rich text structure
}

/**
 * TypeScript interface for FAQ JSON-LD structured data
 * Following schema.org FAQPage specification
 */
interface FAQPageJsonLD {
  '@context': string;
  '@type': 'FAQPage';
  '@id'?: string;
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

/**
 * Converts Contentful rich text to plain text
 * @param richText Contentful rich text document
 * @returns Plain text string
 */
function convertRichTextToPlainText(richText: any): string {
  if (!richText || !richText.content) {
    return '';
  }

  const extractTextFromNode = (node: any): string => {
    if (node.nodeType === 'text') {
      return node.value || '';
    }

    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractTextFromNode).join(' ');
    }

    return '';
  };

  return richText.content
    .map(extractTextFromNode)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generates FAQ JSON-LD structured data for search engines
 * Following schema.org FAQPage specification
 * @param faqs Array of FAQ items
 * @param pageUrl Optional URL of the page containing the FAQs
 * @returns JSON-LD structured data object
 */
export function generateFAQJsonLD(faqs: FAQItem[], pageUrl?: string): FAQPageJsonLD {
  const faqItems = faqs.map(faq => {
    // Convert rich text to plain text if available, otherwise use plain answer
    let answerText = '';
    if (faq.answerRichText) {
      answerText = convertRichTextToPlainText(faq.answerRichText);
    } else if (faq.answer) {
      answerText = faq.answer;
    }

    return {
      '@type': 'Question' as const,
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: answerText
      }
    };
  });

  const jsonLD: FAQPageJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems
  };

  // Add URL if provided
  if (pageUrl) {
    jsonLD['@id'] = pageUrl;
  }

  return jsonLD;
}

/**
 * Generates the JSON-LD script tag for FAQ structured data
 * Following Next.js security recommendations to prevent XSS attacks
 * @param faqs Array of FAQ items
 * @param pageUrl Optional URL of the page containing the FAQs
 * @returns Script tag with JSON-LD data
 */
export function generateFAQJsonLDScript(faqs: FAQItem[], pageUrl?: string): React.ReactElement {
  const jsonLD = generateFAQJsonLD(faqs, pageUrl);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        // Following Next.js recommendation: sanitize < characters to prevent XSS
        __html: JSON.stringify(jsonLD).replace(/</g, '\\u003c')
      }}
    />
  );
}

/**
 * React component for FAQ JSON-LD structured data
 * Following Next.js recommendations for structured data implementation
 */
export function FAQJsonLD({ faqs, pageUrl }: { faqs: FAQItem[]; pageUrl?: string }): React.ReactElement | null {
  if (!faqs || faqs.length === 0) {
    return null;
  }

  return generateFAQJsonLDScript(faqs, pageUrl);
}